import React from 'react';
import { isEqual } from 'lodash';
import i18next from 'i18next';
import { Badge, Grid, styled, Typography, type Breakpoints } from '@mui/material';

import useSettings from '../hooks/useSettings';
import { type FilterItemBaseProps, type FilterType, type MiddlewhereFilterValue } from './FilterItemManager';
import FilterItemOperatorSelectField from './FilterItemOperatorSelectField';

import en from './i18n/en';
import it from './i18n/it';

i18next.addResourceBundle('en', 'core', en);
i18next.addResourceBundle('it', 'core', it);

const filterTypes = [
	{
		id: 'text',
		operators: ['like', 'equals', 'not_equal', 'is_null', 'is_not_null']
	},
	{
		id: 'number',
		operators: [
			'equals',
			'not_equal',
			'greater',
			'greater_equal',
			'smaller',
			'smaller_equal',
			'is_null',
			'is_not_null'
		]
	},
	{
		id: 'decimal',
		operators: [
			'equals',
			'not_equal',
			'greater',
			'greater_equal',
			'smaller',
			'smaller_equal',
			'is_null',
			'is_not_null'
		]
	},
	{
		id: 'currency',
		operators: [
			'equals',
			'not_equal',
			'greater',
			'greater_equal',
			'smaller',
			'smaller_equal',
			'is_null',
			'is_not_null'
		]
	},
	{
		id: 'date',
		operators: [
			'equals',
			'not_equal',
			'greater',
			'greater_equal',
			'smaller',
			'smaller_equal',
			'range',
			'is_null',
			'is_not_null'
		]
	},
	{
		id: 'datetime',
		operators: [
			'equals',
			'not_equal',
			'greater',
			'greater_equal',
			'smaller',
			'smaller_equal',
			'range',
			'is_null',
			'is_not_null'
		]
	},
	{
		id: 'boolean',
		operators: ['all', 'equals', 'not_equal']
	},
	{
		id: 'list',
		operators: ['in', 'not_in', 'is_null', 'is_not_null']
	},
	{
		id: 'child-list',
		operators: ['in', 'not_in']
	},
	{
		id: 'module',
		operators: ['equals', 'not_equal', 'is_null', 'is_not_null']
	},
	{
		id: 'slider',
		operators: ['equals', 'not_equal', 'greater', 'greater_equal', 'smaller', 'smaller_equal']
	},
	{
		id: 'custom',
		operators: ['all', 'equals', 'not_equal']
	}
];

type FilterItemProps<T> = {
	filter: FilterType;
	renderValueComponent: (props: FilterItemBaseProps<T>) => React.ReactNode;
	handleFilterChange: (name: string, operator: string, value: T) => void;
};

const StyledBadge = styled(Badge)(({ invisible, theme }) => ({
	marginLeft: !invisible ? 12 : 0,
	'& > .MuiBadge-badge': {
		top: '50%',
		left: '-8px'
	}
}));

export default function FilterItem(props: FilterItemProps<MiddlewhereFilterValue>) {
	const { t } = useSettings();

	const filterType = filterTypes.find(type => type.id === props.filter.filt_type);

	const [operator, setOperator] = React.useState(
		props.filter.filt_default_clause || props.filter.filt_current_clause || filterType?.operators[0] || 'equals'
	);

	const [value, setValue] = React.useState<MiddlewhereFilterValue | undefined>(
		props.filter.filt_default_value || props.filter.filt_current_value
	);

	const noValueComponent = operator === 'is_null' || operator === 'is_not_null';
	const noValue = !value || value === null || isEqual(value, []);

	const disabledOperator = !!props.filter.filt_default_value || !!props.filter.filt_default_clause;

	const sendChange = (newValue?: MiddlewhereFilterValue, newOperator?: string) => {
		if (newOperator) setOperator(newOperator);
		setValue(newValue);
		props.handleFilterChange(props.filter.filt_field, newOperator || operator, newValue || null);
	};

	const valueComponent = props.renderValueComponent({
		name: props.filter.filt_field,
		operator,
		value,
		defaultValue: props.filter.filt_default_value,
		sendChange
	});

	if (filterType) {
		return (
			<Grid size={props.filter.filt_size || 1}>
				{/* LABEL */}
				<Typography variant="subtitle2" gutterBottom>
					<StyledBadge
						variant="dot"
						color="secondary"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'left'
						}}
						invisible={!noValueComponent && noValue}
					>
						{t(props.filter.filt_label)}
					</StyledBadge>
				</Typography>
				<Grid container spacing={1}>
					{/* CLAUSE */}
					{filterType.id !== 'boolean' && filterType.id !== 'custom' && (
						<Grid
							sx={theme => ({
								width: '100%',
								maxWidth: noValueComponent ? '100%' : 150,
								[theme.breakpoints.down('sm')]: {
									maxWidth: '100%'
								}
							})}
						>
							<FilterItemOperatorSelectField
								name={`${props.filter.filt_field}_operator`}
								operators={filterType.operators}
								value={operator!}
								sendChange={newOperator => sendChange(null, newOperator)}
								disabled={disabledOperator}
							/>
						</Grid>
					)}
					{/* VALUE */}
					{!noValueComponent && <Grid size="grow">{valueComponent}</Grid>}
				</Grid>
			</Grid>
		);
	}

	return <></>;
}
